import { useNavigate } from 'react-router-dom'
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable'
import { Column as TableColumn } from 'primereact/column'
import { Card } from 'primereact/card'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useBoltContext } from '../contexts/BoltContext'
import { useUserContext } from '../contexts/UserContext'
import { buildStructureLink } from '../../tools/link'
import TablePagination from '../table/TablePagination'
import Bolt from '../../models/Bolt'
import { StructureTypes } from '../../models/Structure'
import t, { _ta, _tf } from '../../tools/t'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import ContextHeader from '../common/ContextHeader'
import DateDisplay from '../common/pure/DateDisplay'
import Heading from '../common/pure/Heading'
import {
    getFilterableDateRangeColumnProps,
    getFilterableEnumColumnProps,
    getFilterableNumericRangeColumnProps,
    getFilterableTableProps,
    getSortableColumnProps,
    getSortableTableProps,
} from '../table/table-props'
import { TagIdCopyButton } from '../common/pure/CopyButton'
import { BoltDiameterSettings, BoltTypeSettings, MaterialSettings } from '../../tools/settings'
import { mm } from '../../tools/format'
import { ArchiveButton, RestoreButton, TableActionContainer } from '../table/TableButton'

export default function UnassignedBoltsScreen() {
    const navigate = useNavigate()
    const { unassignedBolts, archiveBolt, restoreBolt } = useBoltContext()
    const { can } = useUserContext()
    const canWriteHardware = can(PermissionSuffix.HardwareWrite)

    const navigateToBolt = (event: DataTableRowClickEvent) => {
        const bolt = event.data as Bolt
        const link = buildStructureLink(StructureTypes.Bolt, bolt.id)
        navigate(link)
    }

    const onArchiveBolt = async (bolt: Bolt) => {
        if (!confirm('Are you sure you want to archive bolt:\n' + bolt.name)) return
        await archiveBolt(bolt.id)
    }

    const onRestoreBolt = async (bolt: Bolt) => {
        await restoreBolt(bolt.id)
    }

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <ContextHeader>
                        <Heading standalone size={2}>
                            {t('unassigned_bolts_list')}
                        </Heading>
                    </ContextHeader>

                    <Card>
                        <DataTable
                            value={unassignedBolts.items}
                            rowClassName={(b: Bolt) =>
                                b.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                            emptyMessage={t('hardware_empty')}
                            onRowClick={navigateToBolt}
                            {...getSortableTableProps(unassignedBolts)}
                            {...getFilterableTableProps()}
                        >
                            <TableColumn
                                header={t('common_tag')}
                                body={(b: Bolt) => <TagIdCopyButton tag={b.currentTag} />}
                            />

                            <TableColumn
                                header={t('bolt_field_batch_no')}
                                body={(b: Bolt) => b.manufacturingDetails?.batch}
                                {...getSortableColumnProps('manufacturingDetails.batch')}
                            />

                            <TableColumn
                                header={t('bolt_field_diameter')}
                                body={(b: Bolt) => _ta(b, 'diameter')}
                                {...getFilterableEnumColumnProps(
                                    'diameter',
                                    unassignedBolts,
                                    BoltDiameterSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('bolt_field_length')}
                                body={(b: Bolt) => mm(b.nominalLength)}
                                {...getFilterableNumericRangeColumnProps(
                                    'nominalLength',
                                    unassignedBolts
                                )}
                            />

                            <TableColumn
                                header={t('bolt_field_material')}
                                body={(b: Bolt) => _tf('', 'material', b.material)}
                                {...getFilterableEnumColumnProps(
                                    'material',
                                    unassignedBolts,
                                    MaterialSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('bolt_field_type')}
                                body={(b: Bolt) => _ta(b, 'type')}
                                {...getFilterableEnumColumnProps(
                                    'type',
                                    unassignedBolts,
                                    BoltTypeSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('life_cycle_field_created_at')}
                                body={(b: Bolt) => (
                                    <DateDisplay value={b.createdAt} target={b.id} />
                                )}
                                {...getSortableColumnProps('createdAt')}
                                {...getFilterableDateRangeColumnProps('createdAt', unassignedBolts)}
                            />

                            {canWriteHardware && (
                                <TableColumn
                                    body={(b: Bolt) => (
                                        <TableActionContainer>
                                            {b.isArchived ? (
                                                <RestoreButton
                                                    target={b.id}
                                                    onClick={() => onRestoreBolt(b)}
                                                />
                                            ) : (
                                                <ArchiveButton
                                                    target={b.id}
                                                    onClick={() => onArchiveBolt(b)}
                                                />
                                            )}
                                        </TableActionContainer>
                                    )}
                                />
                            )}
                        </DataTable>
                        <TablePagination data={unassignedBolts} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
